import { uniqBy } from "lodash-es";
import { clone } from "ramda";
import { PureComponent } from "react";

import { HeaderBar } from "widgets";

import { Checkbox } from "../core/forms/Checkbox.js";
import { SelectAllNoneWidget } from "../core/forms/SelectWidget.js";
import type { IFilterChartData, IFilterChartValue } from "./FilterChart.js";
import type { ColumnAnnotation, StringMapEntry } from "@/graphql.ts";
import type { IColumnAnnotationFilter, IColumnAnnotationFilterMap } from "@/redux/modules/dataset-viewer.ts";

export interface IConditionValuesFilterProps {
  condition: ColumnAnnotation;
  filterData: IFilterChartData;
  filteredConditions: IColumnAnnotationFilterMap;

  filteredConditionsChanged: (conditionField: string, conditionFilter: IColumnAnnotationFilter) => void;
}

interface IConditionValuesFilterState {
  showHiddenValues: boolean;
}

/**
 * Component representing the filter options and histogram of a condition/annotation.
 *
 * created on 4/27/2017
 */
class CategoricalConditionValuesFilter extends PureComponent<IConditionValuesFilterProps, IConditionValuesFilterState> {
  constructor(props: IConditionValuesFilterProps) {
    super(props);

    this.state = {
      showHiddenValues: false,
    };
  }

  selectNone = () => {
    this.props.filteredConditionsChanged(this.props.condition.field, {
      name: this.props.condition.field,
      categoricalValues: this.props.condition.valueSet.slice(),
    });
  };

  selectAll = () => {
    this.props.filteredConditionsChanged(this.props.condition.field, {
      name: this.props.condition.field,
      categoricalValues: null,
    });
  };

  toggleValueFilter = (conditionValue: StringMapEntry) => {
    const columnAnnotationFilter: IColumnAnnotationFilter = clone({
      ...this.props.filteredConditions[this.props.condition.field],
    });
    const filteredValueIndex: number = columnAnnotationFilter?.categoricalValues?.indexOf(conditionValue.value);
    // if the toggled conditionValue is already part of the filtered values list we remove it from there
    if (filteredValueIndex !== null && filteredValueIndex > -1) {
      columnAnnotationFilter.categoricalValues.splice(filteredValueIndex, 1);
    } else {
      // otherwise we add the conditionValue as a new filtered conditionValue
      if (!columnAnnotationFilter.categoricalValues) {
        columnAnnotationFilter.categoricalValues = [];
      }
      columnAnnotationFilter.categoricalValues.push(conditionValue.value);
    }
    // tell the redux state
    this.props.filteredConditionsChanged(this.props.condition.field, columnAnnotationFilter);
  };

  render() {
    const conditionFilter: IColumnAnnotationFilter = this.props.filteredConditions[this.props.condition.field];

    const uniqueConditionValues: StringMapEntry[] = uniqBy(this.props.condition.values, "value");

    const visibleConditionValues: StringMapEntry[] = uniqueConditionValues.filter((condition: StringMapEntry) => {
      const outFilteredValue = !!conditionFilter?.categoricalValues?.find(
        (filteredValue) => condition.value === filteredValue,
      );

      // SEL5-2158 show only the relevant options to be filtered out : if the data corresponding to a category is already filtered out (indirectly by some other filter)
      // we should hide this option from the list
      const alreadyFilteredOut: boolean =
        !outFilteredValue &&
        this.props.filterData.valueSet.find(
          (filterDataValue: IFilterChartValue) => filterDataValue.name === condition.value,
        ).remainingCount <= 0;
      // there is a link that controls a flag for showing all categories anyway
      return this.state.showHiddenValues || !alreadyFilteredOut;
    });

    return (
      <div className="chart-filters-item-values-popover">
        <HeaderBar
          title={this.props.condition.name}
          actions={[
            <SelectAllNoneWidget
              key={"all-none"}
              current={visibleConditionValues.length - (conditionFilter?.categoricalValues?.length ?? 0)}
              max={visibleConditionValues.length}
              onSelectAll={(all) => {
                if (all) {
                  this.selectAll();
                } else {
                  this.selectNone();
                }
              }}
              label={""}
            />,
          ]}
        />
        <div className="chart-filters-item-values">
          {visibleConditionValues.map((conditionValue: StringMapEntry) => {
            const outFilteredValue = !!conditionFilter?.categoricalValues?.find(
              (filteredValue) => conditionValue.value === filteredValue,
            );

            return (
              <div key={`${this.props.condition.field}-${conditionValue.value}`} className="checkbox check-default">
                <Checkbox
                  id={`${this.props.condition.field}-${conditionValue.value}`}
                  checked={!outFilteredValue}
                  label={conditionValue.value}
                  onChange={() => this.toggleValueFilter(conditionValue)}
                  tooltipText={conditionValue.value}
                />
              </div>
            );
          })}
        </div>

        {visibleConditionValues.length !== this.props.condition.valueSet.length && (
          <div>
            <a
              onClick={() => {
                this.setState({ showHiddenValues: true });
              }}
            >
              Show all {this.props.condition.valueSet.length} categories
            </a>
          </div>
        )}
      </div>
    );
  }
}

export default CategoricalConditionValuesFilter;
